import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  Input,
  SimpleTable,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import Tile from "@src/components/containers/Tile";
import Menu, { MenuItem, MenuThemes } from "@src/components/common/Menu";
import StatusBadge, {
  StatusBadgeVariant,
} from "@src/components/common/StatusBadge";
import { useCurrentUserStore } from "@src/state/currentUserState";
import {
  ClinicUserRoles,
  ClinicUserStatuses,
} from "@src/constants/ClinicUserConstants";
import * as TeamApi from "@src/api/TeamApi";
import { TeamMember } from "@src/api/TeamApi";
import InviteMemberModal from "./InviteMemberModal";
import styles from "./ManageTeamPage.module.scss";

type StatusFilter = "all" | "active" | "inactive";

const STATUS_FILTERS: Array<{ label: string; link: StatusFilter }> = [
  { label: "All Statuses", link: "all" },
  { label: "Active", link: "active" },
  { label: "Inactive", link: "inactive" },
];

function statusToVariant(status: TeamMember["status"]): StatusBadgeVariant {
  if (status === ClinicUserStatuses.Pending) return "pending";
  if (status === ClinicUserStatuses.Rejected) return "inactive";
  return "active";
}

function formatLastLogin(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function roleLabel(role: TeamMember["role"]): string {
  return role === ClinicUserRoles.Admin ? "Admin" : "Team Member";
}

export default function ManageTeamPage(): React.JSX.Element {
  const showToast = useToast();
  const { currentUser } = useCurrentUserStore();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [busyMemberId, setBusyMemberId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const activeClinicUser =
    currentUser?.clinicUsers?.find(
      (cu) => cu.clinic.id === currentUser?.activeClinic?.id,
    ) ?? null;
  const isClinicAdmin =
    activeClinicUser?.role === ClinicUserRoles.Admin &&
    activeClinicUser?.status === ClinicUserStatuses.Active;

  const loadMembers = useCallback(async () => {
    try {
      const response = await TeamApi.listTeamMembers();
      setMembers(response.body ?? []);
    } catch (err: any) {
      showToast({
        message:
          err?.response?.body?.error ||
          err?.message ||
          "Could not load team members.",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const updateMember = async (
    clinicUserId: string,
    payload: TeamApi.UpdateTeamMemberPayload,
    successMessage: string,
  ) => {
    setBusyMemberId(clinicUserId);
    try {
      await TeamApi.updateTeamMember(clinicUserId, payload);
      await loadMembers();
      showToast({ message: successMessage, variant: "success" });
    } catch (err: any) {
      showToast({
        message:
          err?.response?.body?.error ||
          err?.message ||
          "Could not update team member.",
        variant: "error",
      });
    } finally {
      setBusyMemberId(null);
    }
  };

  const handleApprove = (m: TeamMember) =>
    updateMember(
      m.id,
      { status: ClinicUserStatuses.Active },
      `${m.fullName} approved.`,
    );

  const handleReject = (m: TeamMember) => {
    if (
      !window.confirm(
        `Reject ${m.fullName}? They will not be able to access this clinic.`,
      )
    )
      return;
    updateMember(
      m.id,
      { status: ClinicUserStatuses.Rejected },
      `${m.fullName} rejected.`,
    );
  };

  const handleChangeRole = (m: TeamMember) => {
    const isPromoting = m.role !== ClinicUserRoles.Admin;
    const newRole = isPromoting
      ? ClinicUserRoles.Admin
      : ClinicUserRoles.Member;
    if (
      !window.confirm(
        `Change ${m.fullName}'s role to ${isPromoting ? "Admin" : "Team Member"}?`,
      )
    )
      return;
    updateMember(
      m.id,
      { role: newRole },
      `${m.fullName} is now ${isPromoting ? "an admin" : "a team member"}.`,
    );
  };

  const handleDeactivate = (m: TeamMember) => {
    if (
      !window.confirm(
        `Deactivate ${m.fullName}? They will lose access to this clinic.`,
      )
    )
      return;
    updateMember(
      m.id,
      { status: ClinicUserStatuses.Rejected },
      `${m.fullName} deactivated.`,
    );
  };

  const pendingMembers = useMemo(
    () => members.filter((m) => m.status === ClinicUserStatuses.Pending),
    [members],
  );

  // Pending users live in the dedicated "Awaiting Approval" inset above —
  // we exclude them from the main table so each row isn't duplicated.
  const visibleMembers = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    return members.filter((m) => {
      if (m.status === ClinicUserStatuses.Pending) return false;
      if (
        statusFilter === "active" &&
        m.status !== ClinicUserStatuses.Active
      )
        return false;
      if (
        statusFilter === "inactive" &&
        m.status !== ClinicUserStatuses.Rejected
      )
        return false;
      if (!searchLower) return true;
      return (
        m.fullName.toLowerCase().includes(searchLower) ||
        m.email.toLowerCase().includes(searchLower)
      );
    });
  }, [members, search, statusFilter]);

  const handleAddTeamMemberClick = () => setInviteOpen(true);

  const selectedFilter =
    STATUS_FILTERS.find((f) => f.link === statusFilter) ?? STATUS_FILTERS[0];

  return (
    <div className={styles.root} data-testid="manage-team-page">
      <Tile>
        <div className={styles.header}>
          <h1 className={styles.title}>Manage My Team</h1>
          <div className={styles.controls}>
            <Input
              defaultValue=""
              value={search}
              onChange={setSearch}
              placeholder="Find team members..."
              width={280}
              height={36}
              icon={<i className="fa-light fa-magnifying-glass" />}
              data-testid="manage-team-page-search"
            />
            <Dropdown
              label="Status"
              selectedLabel={selectedFilter.label}
              items={STATUS_FILTERS}
              onItemClick={(item: { label: string; link: string | number }) =>
                setStatusFilter(item.link as StatusFilter)
              }
              customHeight="36px"
              customWidth="160px"
            />
            {isClinicAdmin && (
              <Button
                className="btn btn-primary btn-md"
                onClick={handleAddTeamMemberClick}
                data-testid="manage-team-page-add-member-button"
              >
                Add Team Member
              </Button>
            )}
          </div>
        </div>

        {pendingMembers.length > 0 && (
          <div
            className={styles.pendingInset}
            data-testid="manage-team-page-pending-inset"
          >
            <div className={styles.pendingTitle}>
              Team Members Awaiting Approval
            </div>
            {pendingMembers.map((member) => (
              <div key={member.id} className={styles.pendingRow}>
                <div className={styles.pendingName}>{member.fullName}</div>
                <div className={styles.pendingEmail}>{member.email}</div>
                <div className={styles.pendingActions}>
                  {isClinicAdmin ? (
                    <>
                      <Button
                        className={`btn btn-sm ${styles.approveButton}`}
                        onClick={() => handleApprove(member)}
                        disabled={busyMemberId === member.id}
                        data-testid={`manage-team-page-approve-${member.id}`}
                      >
                        Approve
                      </Button>
                      <Button
                        className={`btn btn-sm ${styles.rejectButton}`}
                        onClick={() => handleReject(member)}
                        disabled={busyMemberId === member.id}
                        data-testid={`manage-team-page-reject-${member.id}`}
                      >
                        Reject
                      </Button>
                    </>
                  ) : (
                    <span className={styles.cell}>Awaiting admin review</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className={styles.loadingPlaceholder}>Loading team…</div>
        ) : (
          <SimpleTable
            className={styles.table}
            enablePagination
            pageSize={10}
            paginationItemsLabel="Team Members"
            columns={[
              {
                header: "Name",
                key: "fullName",
                render: (m: TeamMember) => (
                  <span className={styles.cell}>{m.fullName}</span>
                ),
              },
              {
                header: "Email Address",
                key: "email",
                render: (m: TeamMember) => (
                  <span className={styles.cell}>{m.email}</span>
                ),
              },
              {
                header: "Role",
                key: "role",
                render: (m: TeamMember) => (
                  <span className={styles.cell}>{roleLabel(m.role)}</span>
                ),
              },
              {
                header: "Status",
                key: "status",
                render: (m: TeamMember) => (
                  <StatusBadge variant={statusToVariant(m.status)} />
                ),
              },
              {
                header: "Last Login",
                key: "lastLogin",
                render: (m: TeamMember) => (
                  <span className={styles.cell}>
                    {formatLastLogin(m.lastLogin)}
                  </span>
                ),
              },
              {
                header: "Action",
                key: "actions",
                render: (m: TeamMember) => {
                  if (!isClinicAdmin) return null;
                  return (
                    <span className="padding-left-sm">
                      <Menu
                        buttonContent={
                          <div className="padding-x-sm">
                            <i
                              className={`fa-light fa-ellipsis-v ${styles.kebab}`}
                            />
                          </div>
                        }
                        theme={MenuThemes.Small}
                      >
                        <MenuItem>
                          <Button
                            className="width-100"
                            label={
                              m.role === ClinicUserRoles.Admin
                                ? "Demote to Team Member"
                                : "Promote to Admin"
                            }
                            onClick={() => handleChangeRole(m)}
                            disabled={busyMemberId === m.id}
                            data-testid={`manage-team-page-change-role-${m.id}`}
                          />
                        </MenuItem>
                        {m.status === ClinicUserStatuses.Active && (
                          <MenuItem>
                            <Button
                              className="width-100"
                              label="Deactivate"
                              color="#F80A33"
                              hoverColor="#FF98AA"
                              onClick={() => handleDeactivate(m)}
                              disabled={busyMemberId === m.id}
                              data-testid={`manage-team-page-deactivate-${m.id}`}
                            />
                          </MenuItem>
                        )}
                      </Menu>
                    </span>
                  );
                },
              },
            ]}
            data={visibleMembers}
          />
        )}
      </Tile>

      {isClinicAdmin && (
        <InviteMemberModal
          isShowing={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onInvited={loadMembers}
        />
      )}
    </div>
  );
}
